import type { ProdantixClient } from 'ProdantixSdk/core/client';
import { ProdantixError } from 'ProdantixSdk/core/errors';
import { SYSTEM_EVENT } from 'ProdantixSdk/core/system-events';
import type { EventProperties } from 'ProdantixSdk/core/types';
import { ANNOTATION_MAX_LENGTH, annotationProperties, type DescriptionSource, isIgnored, isOptedOut, OPT_OUT_ATTRIBUTE, OPT_OUT_CLASS, reportOnce, resolveInteractive } from 'ProdantixSdk/web/annotation';
import { installFormCollection } from 'ProdantixSdk/web/forms/collect';

export interface AutocaptureOptions {
  captureElementText?: boolean;
  clicks?: boolean;
  /** Captures submissions of the site's own forms as `$form_submitted`; on
   *  when the project collects forms. */
  collectForms?: boolean;
  maskSelectors?: readonly string[];
  onError?: (error: unknown) => void;
  pageviews?: boolean;
}

type Capturer = Pick<ProdantixClient, 'capture'>;

const FORM_FIELDS = new Set(['input', 'select', 'textarea']);
const ALWAYS_MASKED_TYPES = new Set(['email', 'password', 'tel']);

interface LocationLike {
  host: string;
  href: string;
  pathname: string;
  search: string;
}

interface DocumentLike {
  addEventListener(type: string, listener: (event: unknown) => void, capture?: boolean): void;
  referrer: string;
  removeEventListener(type: string, listener: (event: unknown) => void, capture?: boolean): void;
  title: string;
}

interface HistoryLike {
  pushState(...args: unknown[]): void;
  replaceState(...args: unknown[]): void;
}

interface WindowLike {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

interface ElementLike {
  getAttribute(name: string): string | null;
  id: string;
  className: unknown;
  matches?(selector: string): boolean;
  parentElement: ElementLike | null;
  querySelector?(selector: string): unknown;
  tagName: string;
  textContent: string | null;
}

function getLocation(): LocationLike | undefined {
  return (globalThis as { location?: LocationLike }).location;
}

function getDocument(): DocumentLike | undefined {
  return (globalThis as { document?: DocumentLike }).document;
}

function getWindow(): WindowLike | undefined {
  const win = (globalThis as { window?: WindowLike }).window;
  return win ?? globalThis;
}

function isElement(value: unknown): value is ElementLike {
  return typeof value === 'object' && value !== null && typeof (value as { tagName?: unknown }).tagName === 'string';
}

function withoutQuery(address: string, base?: string): string | undefined {
  let url: URL;
  try {
    url = new URL(address, base);
  } catch {
    return undefined;
  }
  return url.protocol === 'http:' || url.protocol === 'https:' ? `${url.origin}${url.pathname}` : url.protocol;
}

function urlProperties(): EventProperties {
  const location = getLocation();
  const doc = getDocument();
  const props: EventProperties = {};
  if (location) {
    const current = withoutQuery(location.href);
    if (current) {
      props.$current_url = current;
    }
    props.$pathname = location.pathname;
    props.$host = location.host;
  }
  if (doc) {
    if (doc.title) {
      props.$title = doc.title;
    }
    const referrer = doc.referrer ? withoutQuery(doc.referrer) : undefined;
    if (referrer) {
      props.$referrer = referrer;
    }
  }
  return props;
}

function classesOf(element: ElementLike): string {
  return typeof element.className === 'string' ? element.className : '';
}

function isFormField(element: ElementLike): boolean {
  return FORM_FIELDS.has(element.tagName.toLowerCase()) || ALWAYS_MASKED_TYPES.has((element.getAttribute('type') ?? '').toLowerCase());
}

function elementProperties(element: ElementLike, isMasked: boolean, captureText: boolean): EventProperties {
  const props: EventProperties = {
    $el_tag: element.tagName.toLowerCase(),
    $event_type: 'click',
  };
  if (isMasked) {
    return props;
  }
  const text = captureText ? element.textContent?.trim() : undefined;
  if (text) {
    props.$el_text = text.slice(0, 255);
  }
  if (element.id) {
    props.$el_id = element.id;
  }
  const classes = classesOf(element);
  if (classes) {
    props.$el_classes = classes;
  }
  const href = element.getAttribute('href');
  const stripped = href ? withoutQuery(href, getLocation()?.href) : undefined;
  if (stripped) {
    props.$el_href = stripped;
  }
  return props;
}

function squash(text: string | null | undefined): string {
  return (text ?? '').replace(/\s+/g, ' ').trim();
}

function described(description: string, source: DescriptionSource): EventProperties {
  return { $el_description: description, $el_description_source: source };
}

function inferredDescription(element: ElementLike, isMasked: boolean, captureText: boolean): EventProperties {
  if (!isMasked && captureText) {
    const label = squash(element.getAttribute('aria-label'));
    if (label !== '' && label.length <= ANNOTATION_MAX_LENGTH) {
      return described(label, 'aria_label');
    }
    const text = squash(element.textContent);
    if (text !== '' && text.length <= ANNOTATION_MAX_LENGTH) {
      return described(text, 'text');
    }
  }
  const href = isMasked ? null : element.getAttribute('href');
  const destination = href ? withoutQuery(href, getLocation()?.href) : undefined;
  if (destination) {
    return described(destination, 'href');
  }
  const role = squash(element.getAttribute('role')).toLowerCase();
  return role === '' ? described(element.tagName.toLowerCase(), 'tag') : described(role, 'role');
}

interface MaskCheck {
  holdsMasked: (element: ElementLike) => boolean;
  isMasked: (element: ElementLike) => boolean;
  isOptedOut: (element: ElementLike) => boolean;
}

function createMaskCheck(selectors: readonly string[], onError: (error: unknown) => void): MaskCheck {
  const broken = new Set<string>();

  const tryMatch = (selector: string, match: () => boolean): boolean => {
    if (broken.has(selector)) {
      return false;
    }
    try {
      return match();
    } catch (error) {
      broken.add(selector);
      onError(new ProdantixError('config', `autocapture maskSelectors entry "${selector}" is not a valid selector`, { cause: error }));
      return false;
    }
  };

  const matchesSelector = (element: ElementLike, selector: string): boolean => (element.matches ? tryMatch(selector, () => element.matches?.(selector) === true) : false);

  const holdsMasked = (element: ElementLike): boolean => {
    const find = element.querySelector?.bind(element);
    if (!find) {
      return false;
    }
    return find(`[${OPT_OUT_ATTRIBUTE}], .${OPT_OUT_CLASS}`) !== null || selectors.some(selector => tryMatch(selector, () => find(selector) !== null));
  };

  const isSelected = (element: ElementLike): boolean => {
    for (let node: ElementLike | null = element; node; node = node.parentElement) {
      const current = node;
      if (selectors.some(selector => matchesSelector(current, selector))) {
        return true;
      }
    }
    return false;
  };

  const isInField = (element: ElementLike): boolean => {
    for (let node: ElementLike | null = element; node; node = node.parentElement) {
      if (isFormField(node)) {
        return true;
      }
    }
    return false;
  };

  const optsOut = (element: ElementLike): boolean => isOptedOut(element) || isSelected(element);

  return {
    holdsMasked,
    isMasked: element => optsOut(element) || isInField(element),
    isOptedOut: optsOut,
  };
}

function installPageviews(client: Capturer): () => void {
  const history = (globalThis as { history?: HistoryLike }).history;
  const win = getWindow();
  let lastKey = '';

  const capture = (): void => {
    const location = getLocation();
    const key = location ? `${location.pathname}${location.search}` : '';
    if (key === lastKey) {
      return;
    }
    lastKey = key;
    client.capture(SYSTEM_EVENT.pageview, { properties: urlProperties() });
  };

  capture();

  if (!history || !win) {
    return () => undefined;
  }
  const originalPush = history.pushState.bind(history);
  const originalReplace = history.replaceState.bind(history);
  history.pushState = (...args: unknown[]): void => {
    originalPush(...args);
    capture();
  };
  history.replaceState = (...args: unknown[]): void => {
    originalReplace(...args);
    capture();
  };
  win.addEventListener('popstate', capture);

  return () => {
    history.pushState = originalPush;
    history.replaceState = originalReplace;
    win.removeEventListener('popstate', capture);
  };
}

function installClicks(client: Capturer, options: AutocaptureOptions): () => void {
  const doc = getDocument();
  if (!doc) {
    return () => undefined;
  }
  const onError = options.onError ?? (() => undefined);
  const masks = createMaskCheck(options.maskSelectors ?? [], onError);
  const report = reportOnce(onError);
  const captureText = options.captureElementText ?? false;
  const handler = (event: unknown): void => {
    const target = (event as { target?: unknown }).target;
    if (!isElement(target) || isIgnored(target)) {
      return;
    }
    const element = resolveInteractive(target);
    const isMasked = masks.isMasked(target) || masks.holdsMasked(element);
    const annotated = masks.isOptedOut(target) ? {} : annotationProperties(element, report);
    const description = annotated.$el_description === undefined ? inferredDescription(element, isMasked, captureText) : {};
    client.capture(SYSTEM_EVENT.autocapture, {
      properties: { ...urlProperties(), ...elementProperties(element, isMasked, captureText), ...description, ...annotated },
    });
  };
  doc.addEventListener('click', handler, true);
  return () => doc.removeEventListener('click', handler, true);
}

export function installAutocapture(client: Capturer, options: AutocaptureOptions = {}): () => void {
  const cleanups: Array<() => void> = [];
  if (options.pageviews ?? true) {
    cleanups.push(installPageviews(client));
  }
  if (options.clicks ?? true) {
    cleanups.push(installClicks(client, options));
  }
  if (options.collectForms === true && typeof document !== 'undefined') {
    cleanups.push(installFormCollection(client));
  }
  return () => {
    for (const cleanup of cleanups) {
      cleanup();
    }
  };
}
